import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import {
  useSkillsAndRoles,
  useRoleSkillIds,
  type Skill,
} from '@/hooks/useSkillsAndRoles';
import type { MilestonePresentation } from '@/api/adapter/proposalReviews';
import { parsePlanningWeek } from '@/types/project-command';
import type { DeliveryPreference } from '@/types/delivery-preference';
import {
  currentDetails,
  deliveryWindow,
  plannedMilestoneBudget,
  milestoneTotal,
  weeksIn,
  weekText,
  type PlanningMilestone,
} from './planning-draft';

const deliveryTimes = [
  'Within 1 month',
  '1–3 months from start',
  '3–6 months from start',
  'Specific date',
];
export function MilestonesFormCard({
  delivery,
  coordinatorPercent,
  onDelivery,
  definitions,
  details,
  formId,
  onChange,
  onDetails,
  addMilestone,
  removeMilestone,
  renderTasks,
  footer,
}: {
  delivery: { preference: string; date?: string };
  coordinatorPercent: number | undefined;
  onDelivery: (value: { preference: string; date?: string }) => void;
  definitions: PlanningMilestone[];
  details: MilestonePresentation[];
  formId: string;
  onChange: (index: number, changes: Partial<PlanningMilestone>) => void;
  onDetails: (items: MilestonePresentation[]) => void;
  addMilestone: () => void;
  removeMilestone: (index: number) => void;
  renderTasks: (milestone: PlanningMilestone) => ReactNode;
  footer: ReactNode;
}) {
  const { currency } = useDaoConfig();
  const [customWindows, setCustomWindows] = useState<Record<number, boolean>>(
    {}
  );
  const [customHours, setCustomHours] = useState<Record<string, boolean>>({});
  const [budgetInputs, setBudgetInputs] = useState<Record<number, string>>({});
  const budgetOf = (m: PlanningMilestone) =>
    budgetInputs[m.key] ?? milestoneTotal(m);
  const budgetError = (m: PlanningMilestone) => {
    try {
      plannedMilestoneBudget(m, coordinatorPercent, budgetOf(m));
      return '';
    } catch (error) {
      return (error as Error).message;
    }
  };
  const changeBudget = (
    index: number,
    total: string,
    changes: Partial<PlanningMilestone> = {}
  ) => {
    const milestone = {
      ...definitions[index]!,
      ...changes,
      coordinatorFee: total,
      requirements: (
        changes.requirements ?? definitions[index]!.requirements
      ).map((r) => ({ ...r, budget: '0' })),
    };
    setBudgetInputs((previous) => ({ ...previous, [milestone.key]: total }));
    try {
      onChange(
        index,
        plannedMilestoneBudget(milestone, coordinatorPercent, total)
      );
    } catch {
      onChange(index, milestone);
    }
  };
  const catalog = useSkillsAndRoles();
  const availableRoles =
    catalog.selectableRoles ?? catalog.roles.filter((role) => role.id !== 1);
  const catalogSkills = catalog.skills;
  const skillById = new Map(catalogSkills.map((skill) => [skill.id, skill]));
  const getSkillName = (id: number) => skillById.get(id)?.name;
  const getRoleId = (role: string) => Number(role) || undefined;
  const activeRoleIds = [
    ...new Set(
      definitions
        .flatMap((m) => m.requirements.map((r) => r.roleId))
        .filter(Boolean)
    ),
  ];
  const { data: roleSkillsMap = {} } = useRoleSkillIds(activeRoleIds);
  const milestones = definitions.map((m) => {
    const item = details.find((d) => d.key === m.key);
    const months = [1, 3, 6].findIndex(
      (month) =>
        JSON.stringify(deliveryWindow(m.window, month)) ===
        JSON.stringify(m.window)
    );
    return {
      title: m.title,
      description: item?.description ?? '',
      budget: milestoneTotal(m),
      deliveryTime: String(customWindows[m.key] || months < 0 ? 3 : months),
      profiles: m.requirements.map((r) => {
        const weeklyHours = r.minutes / (60 * Math.max(1, weeksIn(m.window)));
        return {
          role: String(r.roleId || ''),
          skills: r.skillIds.map(String),
          proficiency:
            item?.requirements.find((d) => d.key === r.key)?.experience ?? '',
          availability: customHours[m.key + ':' + r.key]
            ? 'hourly'
            : weeklyHours === 40
              ? 'fulltime'
              : weeklyHours === 20
                ? 'parttime'
                : 'hourly',
          neededHours: String(weeklyHours),
        };
      }),
    };
  });
  const totalProposedBudget = definitions
    .reduce((sum, m) => sum + BigInt(milestoneTotal(m)), 0n)
    .toString();
  const changeRequirement = (
    index: number,
    profile: number,
    changes: Partial<PlanningMilestone['requirements'][number]>
  ) =>
    changeBudget(index, budgetOf(definitions[index]!), {
      requirements: definitions[index]!.requirements.map((r, i) =>
        i === profile ? { ...r, ...changes } : r
      ),
    });
  const updateMilestone = (index: number, field: string, value: string) => {
    if (field === 'title') onChange(index, { title: value });
    if (field === 'description')
      onDetails(
        currentDetails(definitions, details).map((d, i) =>
          i === index ? { ...d, description: value } : d
        )
      );
    if (field === 'deliveryTime')
      setCustomWindows((previous) => ({
        ...previous,
        [definitions[index]!.key]: value === '3',
      }));
    if (field === 'deliveryTime' && Number(value) < 3)
      onChange(index, {
        window: deliveryWindow(
          definitions[index]!.window,
          [1, 3, 6][Number(value)]!
        ),
      });
  };
  const changeWindow = (
    index: number,
    part: 'start' | 'end',
    value: string
  ) => {
    if (value)
      onChange(index, {
        window: {
          ...definitions[index]!.window,
          [part]: parsePlanningWeek(value),
        },
      });
  };
  const handleProfileRoleChange = (
    index: number,
    profile: number,
    role: string
  ) => changeRequirement(index, profile, { roleId: Number(role) });
  const updateProfile = (
    index: number,
    profile: number,
    field: string,
    value: string | string[]
  ) => {
    if (field === 'proficiency')
      onDetails(
        currentDetails(definitions, details).map((d, i) =>
          i === index
            ? {
                ...d,
                requirements: d.requirements.map((r, j) =>
                  j === profile
                    ? {
                        ...r,
                        experience: (value ||
                          null) as MilestonePresentation['requirements'][number]['experience'],
                      }
                    : r
                ),
              }
            : d
        )
      );
    if (field === 'availability')
      setCustomHours((previous) => ({
        ...previous,
        [definitions[index]!.key +
        ':' +
        definitions[index]!.requirements[profile]!.key]: value === 'hourly',
      }));
    if (field === 'availability' && value !== 'hourly')
      changeRequirement(index, profile, {
        minutes:
          (value === 'fulltime' ? 40 : 20) *
          60 *
          Math.max(1, weeksIn(definitions[index]!.window)),
      });
    if (field === 'neededHours')
      changeRequirement(index, profile, {
        minutes: Math.round(
          Number(value) * 60 * Math.max(1, weeksIn(definitions[index]!.window))
        ),
      });
  };
  const toggleProfileSkill = (
    index: number,
    profile: number,
    skill: string
  ) => {
    const ids = definitions[index]!.requirements[profile]!.skillIds;
    const id = Number(skill);
    changeRequirement(index, profile, {
      skillIds: ids.includes(id)
        ? ids.filter((value) => value !== id)
        : [...ids, id],
    });
  };
  const addProfile = (index: number) =>
    changeBudget(index, budgetOf(definitions[index]!), {
      requirements: [
        ...definitions[index]!.requirements,
        {
          key:
            Math.max(
              -1,
              ...definitions[index]!.requirements.map((r) => r.key)
            ) + 1,
          roleId: 0,
          skillIds: [],
          minutes: 0,
          budget: '0',
        },
      ],
    });
  const removeProfile = (index: number, profile: number) =>
    changeBudget(index, budgetOf(definitions[index]!), {
      requirements: definitions[index]!.requirements.filter(
        (_, i) => i !== profile
      ),
    });
  return (
    <div className="milestonesFormBlock scopeSelectors">
      {/* Title Header */}
      <h2>Milestones & Budget</h2>

      {/* Vertical timeline relative wrapper */}
      <div className="relative ml-4 space-y-12 pl-10">
        {milestones.map((m, index) => (
          <div key={definitions[index]!.key} className="milestoneFormCard">
            {/* DOT - Timeline dot */}
            <div className="absolute -left-[45px] top-1.5 z-10 h-1.5 w-1.5 rounded-full bg-transparent outline outline-[6px] outline-offset-1 outline-[--base-fill-3]"></div>

            {/* LINE - Timeline connecting line to the next dot */}
            {index < milestones.length - 1 && (
              <div className="absolute -left-[42px] bottom-[-48px] top-[16px] z-0 w-[2px] bg-[--base-border]" />
            )}

            {/* Milestone header index */}
            <h3 className="milestoneHeader">Milestone {index + 1}</h3>

            {/* Row: Fields (Title/Desc on left, Budget/Type/Trash on right) */}
            <div className="milestoneInfoRow">
              {/* Left Column (Title & Description stacked) */}
              <div className="inputCol flex-col xl:w-[55%]">
                <input
                  className="input"
                  type="text"
                  aria-label="Milestone title"
                  form={formId}
                  placeholder="Milestone title"
                  value={m.title}
                  onChange={(e) =>
                    updateMilestone(index, 'title', e.target.value)
                  }
                  required
                />
                <textarea
                  aria-label={'Description for ' + m.title}
                  form={formId}
                  placeholder="Milestone description"
                  value={m.description}
                  onChange={(e) =>
                    updateMilestone(index, 'description', e.target.value)
                  }
                  rows={3}
                  className="resize-none"
                />
              </div>

              {/* Right Column (Budget, Type & Remove Action button aligned) */}
              <div className="inputCol">
                {/* The total is allocated using planned worker and coordinator minutes. */}
                <div className="input relative flex-1 overflow-hidden rounded-[--radi-6]">
                  <span className="pointer-events-none absolute right-0 top-1/2 flex h-full w-fit -translate-y-[50%] items-center bg-[--base-fill-1] px-[--spacing-7] py-[--spacing-4] text-base font-medium text-[--text-dark-secondary]">
                    {currency.symbol}
                  </span>
                  <input
                    className="w-full"
                    aria-label={`Milestone budget (${currency.symbol})`}
                    type="text"
                    form={formId}
                    required
                    inputMode="numeric"
                    pattern="0|[1-9][0-9]*"
                    aria-invalid={!!budgetError(definitions[index]!)}
                    aria-describedby={
                      budgetError(definitions[index]!)
                        ? formId + '-budget-' + index
                        : undefined
                    }
                    ref={(input) =>
                      input?.setCustomValidity(budgetError(definitions[index]!))
                    }
                    placeholder="Amount"
                    value={budgetOf(definitions[index]!)}
                    onChange={(event) =>
                      changeBudget(index, event.target.value)
                    }
                  />
                </div>

                {/* Delivery time dropdown select */}
                <div className="flex-1">
                  <select
                    aria-label="Delivery time"
                    form={formId}
                    className="w-full"
                    value={m.deliveryTime}
                    onChange={(e) =>
                      updateMilestone(index, 'deliveryTime', e.target.value)
                    }
                  >
                    <option value="">Delivery time</option>
                    {deliveryTimes.map((dt, dtIndex) => (
                      <option key={dt} value={String(dtIndex)}>
                        {dt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Delete button icon */}
                {milestones.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => removeMilestone(index)}
                    variant="outline"
                    className="hover:border-[--state-danger-active] [&>i]:hover:text-[--state-danger-active]"
                    title="Remove Milestone"
                    aria-label="Remove milestone"
                  >
                    <i className="ri-delete-bin-line text-lg" />
                  </Button>
                )}
              </div>
            </div>
            {budgetError(definitions[index]!) && (
              <p
                role="alert"
                id={formId + '-budget-' + index}
                className="text-[--state-danger-active]"
              >
                {budgetError(definitions[index]!)}
              </p>
            )}

            {renderTasks(definitions[index]!)}

            {/* Required Professional Profiles Section (1...n per milestone) */}
            <div className="requiredProfessionalRow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-[--spacing-5]">
                  {/* <i className="ri-user-settings-line text-[--state-brand-active] text-lg" /> */}
                  <h4 className="text-base font-semibold text-[--text-dark-primary]">
                    Required Professional Profiles
                  </h4>
                  <span className="text-xs text-[--text-dark-tertiary]">
                    ({m.profiles.length + 1})
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => addProfile(index)}
                  className="hover:text-[--state-brand-active]/80 font-medium text-[--state-brand-active]"
                >
                  <i className="ri-add-line mr-1 text-sm !text-[--state-brand-active]" />{' '}
                  Add Profile
                </Button>
              </div>

              {/* Profiles List */}
              <div
                className="professionalProfile relative flex items-start gap-[--spacing-10] md:items-center"
                role="group"
                aria-label="Coordinator planning"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[--base-border] text-xs font-semibold text-[--state-brand-active]">
                  <i className="ri-user-smile-line text-[20px] text-[--base-fill-5]" />
                </div>
                <div className="professionalFields flex w-full min-w-0 flex-wrap items-center gap-[--spacing-12]">
                  <span className="w-44 text-sm font-medium">Coordinator</span>
                  <label className="inputGroup">
                    Planned hours for this milestone
                    <input
                      form={formId}
                      type="number"
                      required
                      min="0"
                      step="any"
                      aria-label="Coordination hours"
                      value={definitions[index]!.coordinatorMinutes / 60}
                      onChange={(event) =>
                        changeBudget(index, budgetOf(definitions[index]!), {
                          coordinatorMinutes: Math.round(
                            Number(event.target.value) * 60
                          ),
                        })
                      }
                    />
                  </label>
                </div>
              </div>
              {m.profiles.map((prof, profIdx) => (
                <div
                  key={definitions[index]!.requirements[profIdx]!.key}
                  role="group"
                  aria-label={'Staffing slot ' + (profIdx + 1)}
                  className="professionalProfile relative flex items-start gap-[--spacing-10] md:items-center"
                >
                  {/* Avatar / Index Indicator */}
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[--base-border] text-xs font-semibold text-[--state-brand-active]">
                    <i className="ri-user-smile-line text-[20px] text-[--base-fill-5]" />
                  </div>

                  <div className="professionalFields flex w-full min-w-0 flex-col flex-wrap items-center gap-[--spacing-12] md:flex-row">
                    {/* Role Dropdown */}
                    <div className="w-full md:w-44">
                      <select
                        aria-label="Role"
                        required
                        form={formId}
                        value={
                          availableRoles.find(
                            (role) =>
                              role.name.toLowerCase() ===
                                (prof.role || '').toLowerCase() ||
                              String(role.id) === String(prof.role)
                          )?.id ??
                          prof.role ??
                          ''
                        }
                        onChange={(e) =>
                          handleProfileRoleChange(
                            index,
                            profIdx,
                            e.target.value
                          )
                        }
                        className="flex h-10 w-full rounded-lg border border-[--base-border] bg-[--base-fill-1] px-3 py-2 text-sm text-[--text-dark-primary] focus:border-[--state-brand-active] focus:outline-none"
                      >
                        <option value="">Select Role</option>
                        {prof.role &&
                          !availableRoles.some(
                            (role) => String(role.id) === prof.role
                          ) && (
                            <option value={prof.role}>
                              Unavailable role #{prof.role}
                            </option>
                          )}
                        {availableRoles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name.charAt(0).toUpperCase() +
                              role.name.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Proficiency Dropdown */}
                    <div className="w-full md:w-36">
                      <select
                        aria-label="Requested experience"
                        form={formId}
                        value={prof.proficiency}
                        onChange={(e) =>
                          updateProfile(
                            index,
                            profIdx,
                            'proficiency',
                            e.target.value
                          )
                        }
                        className="flex h-10 w-full rounded-lg border border-[--base-border] bg-[--base-fill-1] px-3 py-2 text-sm text-[--text-dark-primary] focus:border-[--state-brand-active] focus:outline-none"
                      >
                        <option value="">Not specified</option>
                        <option value="Junior">Junior</option>
                        <option value="MidLevel">Mid-level</option>
                        <option value="Senior">Senior</option>
                      </select>
                    </div>

                    {/* Availability Dropdown */}
                    <div className="w-full md:w-40">
                      <select
                        aria-label="Availability"
                        form={formId}
                        value={prof.availability}
                        onChange={(e) =>
                          updateProfile(
                            index,
                            profIdx,
                            'availability',
                            e.target.value
                          )
                        }
                        className="flex h-10 w-full rounded-lg border border-[--base-border] bg-[--base-fill-1] px-3 py-2 text-sm text-[--text-dark-primary] focus:border-[--state-brand-active] focus:outline-none"
                      >
                        <option value="">Select Availability</option>
                        <option value="fulltime">Full Time</option>
                        <option value="parttime">Part Time</option>
                        <option value="hourly">Hours per Week</option>
                      </select>
                    </div>

                    {/* Hours input if hourly */}
                    {prof.availability === 'hourly' && (
                      <div className="w-full md:w-28">
                        <input
                          type="number"
                          aria-label="Hours per week"
                          form={formId}
                          min="0"
                          max="168"
                          step="any"
                          placeholder="Hrs / wk"
                          value={prof.neededHours || ''}
                          onChange={(e) =>
                            updateProfile(
                              index,
                              profIdx,
                              'neededHours',
                              e.target.value
                            )
                          }
                          className="flex h-10 w-full rounded-lg border border-[--base-border] bg-[--base-fill-1] px-3 py-2 text-sm text-[--text-dark-primary] focus:border-[--state-brand-active] focus:outline-none"
                        />
                      </div>
                    )}

                    {/* Skills select & badges */}
                    <div className="chipListInput w-full min-w-[200px] flex-1 bg-[--base-surface-1]">
                      {prof.skills.map((skill) => {
                        const numeric = Number(skill);
                        const displayName =
                          (Number.isInteger(numeric) && numeric > 0
                            ? getSkillName(numeric)
                            : undefined) ??
                          catalogSkills.find(
                            (s) =>
                              s.name.toLowerCase() === skill.toLowerCase() ||
                              String(s.id) === skill
                          )?.name ??
                          'Unavailable skill #' + skill;
                        return (
                          <span key={skill} className="chipItem">
                            {displayName}
                            <Button
                              type="button"
                              variant="ghost"
                              size="fit"
                              aria-label={'Remove ' + displayName}
                              onClick={() =>
                                toggleProfileSkill(index, profIdx, skill)
                              }
                              className="items-center"
                            >
                              &times;
                            </Button>
                          </span>
                        );
                      })}

                      {(() => {
                        const profileRoleId = getRoleId(prof.role);
                        const profileSkillIds = profileRoleId
                          ? roleSkillsMap[profileRoleId] || []
                          : [];
                        const profileAllowedSkills = profileSkillIds
                          .map((sId) => skillById.get(sId))
                          .filter((s): s is Skill => Boolean(s));

                        if (!prof.role) {
                          return (
                            <span className="px-2 py-1 text-xs italic text-[--text-dark-tertiary]">
                              Select role first
                            </span>
                          );
                        }

                        return (
                          <select
                            aria-label="Add skill"
                            form={formId}
                            required={prof.skills.length === 0}
                            value=""
                            onChange={(e) => {
                              const selected = e.target.value;
                              if (
                                selected &&
                                !prof.skills.some(
                                  (ps) =>
                                    ps.toLowerCase() === selected.toLowerCase()
                                )
                              ) {
                                toggleProfileSkill(index, profIdx, selected);
                              }
                              e.target.value = '';
                            }}
                            className="addSkillItem !min-h-[16px] border-none bg-transparent bg-none p-0 text-base leading-none hover:cursor-pointer hover:text-[--state-brand-active]"
                          >
                            <option value="">+ Add skill</option>
                            {profileAllowedSkills
                              .filter((s) => {
                                return !prof.skills.some((ps) => {
                                  if (
                                    ps.toLowerCase() === s.name.toLowerCase() ||
                                    ps === String(s.id)
                                  ) {
                                    return true;
                                  }
                                  const num = Number(ps);
                                  if (Number.isInteger(num) && num > 0) {
                                    return (
                                      getSkillName(num)?.toLowerCase() ===
                                      s.name.toLowerCase()
                                    );
                                  }
                                  return false;
                                });
                              })
                              .map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                          </select>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Remove Profile Button */}
                  {m.profiles.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="fit"
                      onClick={() => removeProfile(index, profIdx)}
                      className="p-2.5 text-[--text-dark-tertiary] hover:border-[--state-danger-active] hover:text-[--state-danger-active]"
                      title="Remove Professional Profile"
                      aria-label="Remove staffing slot"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <details className="scopeCoordination">
              <summary>Schedule and coordination</summary>
              <div className="grid gap-4 sm:grid-cols-2">
                <label>
                  Starting week
                  <input
                    form={formId}
                    type="week"
                    required
                    aria-label="Starting ISO week"
                    value={weekText(definitions[index]!.window.start)}
                    onChange={(event) =>
                      changeWindow(index, 'start', event.target.value)
                    }
                  />
                </label>
                <label>
                  Ending week
                  <input
                    form={formId}
                    type="week"
                    required
                    aria-label="Ending ISO week"
                    value={weekText(definitions[index]!.window.end)}
                    onChange={(event) =>
                      changeWindow(index, 'end', event.target.value)
                    }
                  />
                </label>
              </div>
            </details>
          </div>
        ))}
      </div>

      {/* Add Milestone action */}
      <div>
        <Button
          type="button"
          onClick={addMilestone}
          variant="ghost"
          className="text-[--state-brand-active] hover:text-[--state-brand-active] [&>i]:text-[--state-brand-active]"
        >
          <i className="ri-add-line text-lg text-[--state-brand-active]" /> Add
          Milestone
        </Button>
      </div>

      <div className="border-t border-[--base-border]" />

      <div className="flex flex-col gap-[--spacing-14]">
        <h3 className="text-[18px] font-semibold text-white">Budget</h3>
        <div className="flex flex-wrap gap-12">
          <div className="flex w-[256px] flex-col gap-3">
            <span className="text-base font-medium text-[--text-dark-secondary]">
              Total Estimated Budget
            </span>
            <div className="input flex items-center justify-between">
              <strong>{totalProposedBudget}</strong>
              <span>{currency.symbol}</span>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <span className="text-base font-medium text-[--text-dark-secondary]">
              Delivery time
            </span>
            <div className="flex flex-col gap-3 pl-1">
              {(
                [
                  'WithinOneMonth',
                  'OneToThreeMonths',
                  'ThreeToSixMonths',
                  'SpecificDate',
                ] as DeliveryPreference['preference'][]
              ).map((preference, index) => (
                <div
                  key={preference}
                  className="grid grid-cols-2 gap-[--spacing-12]"
                >
                  <label className="flex w-fit min-w-fit cursor-pointer select-none items-center gap-[--spacing-7] text-nowrap text-base font-medium text-[--text-dark-primary]">
                    <input
                      form={formId}
                      type="radio"
                      name="overallDeliveryTime"
                      checked={delivery.preference === preference}
                      onChange={() =>
                        onDelivery({
                          preference,
                          ...(preference === 'SpecificDate'
                            ? { date: delivery.date ?? '' }
                            : {}),
                        })
                      }
                      className="h-4 w-4 bg-transparent accent-[--state-brand-active]"
                    />
                    {
                      [
                        'Within 1 month',
                        '1-3 months from start',
                        '3-6 months from start',
                        'Specific date',
                      ][index]
                    }
                  </label>
                  {preference === 'SpecificDate' &&
                    delivery.preference === preference && (
                      <div className="relative max-w-[220px]">
                        <i
                          aria-hidden="true"
                          className="ri-calendar-line absolute right-2 top-1/2 -translate-y-[50%] text-[--base-fill-5]"
                        />
                        <input
                          form={formId}
                          type="date"
                          aria-label="Overall delivery date"
                          value={delivery.date ?? ''}
                          onChange={(event) =>
                            onDelivery({ preference, date: event.target.value })
                          }
                          required
                          className="style-none flex h-10 w-full rounded-lg border border-[--base-border] bg-[#141414] px-3 py-2 text-sm text-white focus:border-[#36D399] focus:outline-none"
                        />
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {footer}
    </div>
  );
}
